import { SimpleModal } from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import { FC, ReactNode } from 'react'
import { Step, useSteppedModal, ValidationItem } from './useSteppedModal'
import { logError } from '@sequencehq/utils'

export type SteppedModalProps = {
  onClose: () => void
  onFinalConfirm: () => Promise<void>
  submitting: boolean
  submitDisabled?: boolean
  validation: ValidationItem[]
  canSkip?: boolean
  validationSummaryText: string
  confirmationSummaryText: ReactNode
  confirmTitleText: string
  confirmActionText: string
}

/**
 * The modal is visible for as long as it is mounted: closing it is the
 * parent's responsibility, either via an action calling `onClose` or by
 * unmounting once `onFinalConfirm` completes.
 */
export const SteppedModal = (props: SteppedModalProps) => {
  const { activeStep, transitionToNextStep } = useSteppedModal(props)

  const ActiveComponent = activeStep?.component

  if (!activeStep || !ActiveComponent) {
    return null
  }

  return match(activeStep.wrapper)
    .with('MODAL', () => <ActiveComponent {...activeStep.componentProps} />)
    .with('DIALOG', () => (
      <InformationDialog
        activeStep={activeStep}
        onConfirm={transitionToNextStep}
        onClose={props.onClose}
        isSubmitting={props.submitting}
        submitDisabled={props.submitDisabled}
      >
        <ActiveComponent {...activeStep.componentProps} />
      </InformationDialog>
    ))
    .exhaustive()
}

const InformationDialog: FC<{
  activeStep: Step
  children: ReactNode
  onConfirm: () => void
  onClose: () => void
  isSubmitting: boolean
  submitDisabled?: boolean
}> = ({
  activeStep,
  children,
  onConfirm,
  onClose,
  isSubmitting,
  submitDisabled
}) => {
  const confirmButtonText = activeStep.actions?.confirm.text ?? 'Save'

  return (
    <SimpleModal
      variant="dialog"
      isOpen
      data-testid="schedule.confirmStart"
      title={activeStep.modalTitle}
      showCancelButton={activeStep.actions?.cancel?.canSkip}
      cancelButtonText={activeStep.actions?.cancel.text ?? 'Cancel'}
      submitButtonText={confirmButtonText}
      submittingButtonText={confirmButtonText}
      submitting={isSubmitting}
      submitDisabled={submitDisabled}
      disableSubmitOnEnter
      onClose={() => {
        // Dismissal via ESC/overlay fires both the cancel and close actions,
        // matching the legacy dialog behaviour.
        activeStep.actions?.cancel.onAction().catch(e => logError(e))
        activeStep.actions?.close
          ?.onAction()
          .catch(e => logError(e))
          .finally(onClose)
      }}
      onCancel={() => {
        activeStep.actions?.cancel.onAction().catch(e => logError(e))
      }}
      onSubmit={() => {
        // Not awaited: the confirm action either resolves immediately and
        // advances to the next step, or (on the final step) drives the
        // `submitting` spinner until the parent unmounts the modal.
        activeStep.actions?.confirm
          .onAction()
          .then(() => onConfirm())
          .catch(e => logError(e))

        return false
      }}
    >
      {children}
    </SimpleModal>
  )
}
